import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Search, Menu,  Info, Calendar, Phone, Book } from 'lucide-react';

const UniversityOfVirginia: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "University of Virginia";

    return (
        <div className="min-h-screen bg-white font-serif text-[#232D4B] selection:bg-[#E57200] selection:text-white">
             {/* Header */}
            <nav className="bg-white py-4 px-6 md:px-12 border-b-4 border-[#E57200] sticky top-0 z-50">
                <div className="max-w-[1200px] mx-auto flex justify-between items-center">
                    <div className="flex flex-col">
                        <span className="text-3xl font-normal tracking-wide text-[#232D4B] uppercase">{schoolName}</span>
                    </div>

                    <div className="hidden lg:flex gap-8 font-sans font-bold text-[#E57200] text-lg uppercase tracking-widest items-center">
                        <a href="#" className="hover:text-[#232D4B] transition-colors text-xs">About</a>
                        <a href="#" className="hover:text-[#232D4B] transition-colors text-xs">Admission</a>
                        <a href="#" className="hover:text-[#232D4B] transition-colors text-xs">Academics</a>
                        <a href="#" className="hover:text-[#232D4B] transition-colors text-xs">Research</a>
                        <a href="#" className="hover:text-[#232D4B] transition-colors text-xs">Health System</a>
                         <Search className="text-[#232D4B] cursor-pointer" size={20} />
                    </div>

                    <div className="lg:hidden text-[#232D4B]">
                        <Menu size={28} />
                    </div>
                </div>
            </nav>

            {/* Hero - The Rotunda */}
            <header className="relative h-[650px]">
                <img 
                    src="https://images.unsplash.com/photo-1629813276326-0e06005c21df?q=80&w=1974&auto=format&fit=crop" 
                    alt="The Rotunda" 
                    className="absolute inset-0 w-full h-full object-cover"
                />
                 <div className="absolute inset-0 bg-gradient-to-br from-[#232D4B]/60 to-transparent"></div>

                 <div className="absolute bottom-16 left-0 w-full px-8 md:px-12">
                     <div className="max-w-[1200px] mx-auto">
                        <h1 className="text-4xl md:text-6xl font-normal text-white mb-6 leading-tight max-w-3xl">
                             "This institution will be based on the illimitable freedom of the human mind."
                        </h1>
                        <p className="text-white/80 font-sans font-bold uppercase tracking-widest text-sm mb-8">— Thomas Jefferson, Founder</p>
                         <Button className="bg-[#E57200] hover:bg-[#c06000] text-white font-sans font-bold uppercase tracking-widest px-8 h-12 rounded-sm text-xs">
                             Visit Grounds
                         </Button>
                     </div>
                 </div>
            </header>

            {/* Content Blocks - Clean & Classical */}
            <section className="py-24 px-6 md:px-12 bg-white">
                <div className="max-w-[1200px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-12">
                     <div className="col-span-1 md:col-span-2">
                         <h2 className="text-3xl font-normal text-[#232D4B] mb-8 border-b border-gray-200 pb-4">{schoolName.split(' ')[0]} Today</h2>
                         
                         <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                             <div className="group cursor-pointer">
                                 <img src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?q=80&w=2070&auto=format&fit=crop" className="w-full h-48 object-cover mb-4" alt="News 1"/>
                                 <h3 className="text-xl font-bold text-[#232D4B] group-hover:text-[#E57200] transition-colors mb-2">
                                     Researchers identify key protein in battling autoimmune disease
                                 </h3>
                                 <p className="font-sans text-gray-500 text-sm">School of Medicine</p>
                             </div>
                              <div className="group cursor-pointer">
                                 <img src="https://images.unsplash.com/photo-1524178232363-1fb2b075b655?q=80&w=2070&auto=format&fit=crop" className="w-full h-48 object-cover mb-4" alt="News 2"/>
                                 <h3 className="text-xl font-bold text-[#232D4B] group-hover:text-[#E57200] transition-colors mb-2">
                                     Class of 2029 boasts highest academic credentials in history
                                 </h3>
                                 <p className="font-sans text-gray-500 text-sm">Admission</p>
                             </div>
                         </div>
                     </div>

                     <div className="bg-[#f7f7f7] p-8 border-t-4 border-[#232D4B]">
                         <h3 className="text-xl font-bold text-[#e57200] mb-6 uppercase tracking-widest font-sans">Quick Links</h3>
                         <ul className="space-y-4 font-sans font-bold text-[#232D4B] text-sm">
                             <li><a href="#" className="hover:text-[#E57200] flex items-center gap-2"><Info size={16}/> Student Life</a></li>
                             <li><a href="#" className="hover:text-[#E57200] flex items-center gap-2"><Calendar size={16}/> Academic Calendar</a></li>
                             <li><a href="#" className="hover:text-[#E57200] flex items-center gap-2"><Phone size={16}/> Emergency Contacts</a></li>
                             <li><a href="#" className="hover:text-[#E57200] flex items-center gap-2"><Book size={16}/> Libraries</a></li>
                         </ul>

                         <div className="mt-8 pt-8 border-t border-gray-300">
                             <h4 className="font-bold text-[#232D4B] mb-2">Honor Code</h4>
                             <p className="text-gray-600 text-sm font-sans">
                                 The Honor System is one of the University's most cherished traditions.
                             </p>
                         </div>
                     </div>
                </div>
            </section>

             {/* Footer */}
             <footer className="bg-[#232D4B] text-white py-16 px-6 md:px-12">
                 <div className="max-w-[1200px] mx-auto flex flex-col items-center text-center">
                      <span className="text-2xl uppercase tracking-widest mb-2 font-normal">{schoolName}</span>
                      <p className="font-sans text-gray-400 text-sm mb-8">Charlottesville, VA | 434-924-0311</p>
                      
                      <div className="flex flex-wrap justify-center gap-8 font-sans font-bold text-xs uppercase tracking-widest text-[#E57200] mb-8">
                          <a href="#" className="hover:text-white">Maps</a>
                          <a href="#" className="hover:text-white">Web Policy</a>
                          <a href="#" className="hover:text-white">Report a Barrier</a>
                          <a href="#" className="hover:text-white">Privacy</a>
                          <a href="#" className="hover:text-white">Emergency</a>
                      </div>

                      <div className="text-[10px] uppercase tracking-widest text-gray-500 font-sans">
                          © 2026 by the Rector and Visitors of {schoolName}
                      </div>
                 </div>
             </footer>
        </div>
    );
};

export default UniversityOfVirginia;
